import { createFileRoute } from '@tanstack/react-router';
import { ADMIN_SEARCH_MAX } from '@leanin/shared/admin-search';
import { RESOURCE_KINDS, type ResourceKind } from '@leanin/shared/resource-kinds';
import { RESOURCE_DESIGNED_FOR } from '@leanin/shared/resource-designed-for';
import { AdminResourcesView } from '@/components/admin/AdminResourcesView';
import type {
  AdminResourceContentLayer,
  AdminResourceDesignedFor,
  AdminResourceScope,
  AdminResourceStatus,
  ListAdminResourcesInput,
} from '@/lib/queries/admin';

export type AdminLibraryType = 'resource' | 'track' | 'collection';

interface ResourcesSearch {
  readonly status?: AdminResourceStatus;
  readonly q?: string;
  readonly layer?: AdminResourceContentLayer;
  readonly kind?: ResourceKind;
  readonly type?: AdminLibraryType;
  readonly scope?: AdminResourceScope;
  readonly audience?: AdminResourceDesignedFor;
  readonly cursor?: string;
}

const ALLOWED_STATUS: ReadonlyArray<AdminResourceStatus> = [
  'all',
  'draft',
  'published',
  'featured',
];

const ALLOWED_LAYER: ReadonlyArray<AdminResourceContentLayer> = [
  'community',
  'network',
  'public',
];

const ALLOWED_KIND: ReadonlyArray<ResourceKind> = RESOURCE_KINDS;

const ALLOWED_TYPE: ReadonlyArray<AdminLibraryType> = [
  'resource',
  'track',
  'collection',
];

const ALLOWED_AUDIENCE: ReadonlyArray<AdminResourceDesignedFor> = [
  ...RESOURCE_DESIGNED_FOR,
  'unset',
];

const ALLOWED_SCOPE: ReadonlyArray<AdminResourceScope> = [
  'leadership_library',
  'circle_leader_tips',
  'network_leader_resources',
];

export const Route = createFileRoute('/_authed/admin/resources/')({
  validateSearch: (raw: Record<string, unknown>): ResourcesSearch => {
    const status =
      typeof raw.status === 'string' &&
      (ALLOWED_STATUS as ReadonlyArray<string>).includes(raw.status)
        ? (raw.status as AdminResourceStatus)
        : undefined;
    // An oversize `q` would 422 the whole list, so it reads as absent.
    const q =
      typeof raw.q === 'string' &&
      raw.q.length > 0 &&
      raw.q.length <= ADMIN_SEARCH_MAX
        ? raw.q
        : undefined;
    const layer =
      typeof raw.layer === 'string' &&
      (ALLOWED_LAYER as ReadonlyArray<string>).includes(raw.layer)
        ? (raw.layer as AdminResourceContentLayer)
        : undefined;
    const kind =
      typeof raw.kind === 'string' &&
      (ALLOWED_KIND as ReadonlyArray<string>).includes(raw.kind)
        ? (raw.kind as ResourceKind)
        : undefined;
    const type =
      typeof raw.type === 'string' &&
      (ALLOWED_TYPE as ReadonlyArray<string>).includes(raw.type)
        ? (raw.type as AdminLibraryType)
        : undefined;
    const scope =
      typeof raw.scope === 'string' &&
      (ALLOWED_SCOPE as ReadonlyArray<string>).includes(raw.scope)
        ? (raw.scope as AdminResourceScope)
        : undefined;
    const audience =
      typeof raw.audience === 'string' &&
      (ALLOWED_AUDIENCE as ReadonlyArray<string>).includes(raw.audience)
        ? (raw.audience as AdminResourceDesignedFor)
        : undefined;
    const cursor =
      typeof raw.cursor === 'string' && raw.cursor.length > 0
        ? raw.cursor
        : undefined;
    // Rejected keys are returned as explicit `undefined`: the router merges
    // this result over the raw URL search, so an omitted key would let the
    // raw value through to `useSearch()` and on into the list request.
    return { status, q, layer, kind, type, scope, audience, cursor };
  },
  component: AdminResourcesPage,
});

function AdminResourcesPage() {
  const search = Route.useSearch();
  const activeStatus: AdminResourceStatus = search.status ?? 'all';
  const input: ListAdminResourcesInput = {
    pageSize: 25,
    status: activeStatus,
    ...(search.layer ? { contentLayer: search.layer } : {}),
    ...(search.kind ? { kind: search.kind } : {}),
    ...(search.scope ? { scope: search.scope } : {}),
    ...(search.audience ? { designedFor: search.audience } : {}),
    ...(search.q ? { q: search.q } : {}),
    ...(search.cursor ? { cursor: search.cursor } : {}),
  };

  return <AdminResourcesView input={input} searchParams={search} />;
}
